import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Avatar,
  Box,
  Button,
  createTheme,
  Grid2,
  IconButton,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
  Link,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DocumentSnapshot } from 'firebase/firestore';

import { getInput } from '../../api/firebase/firestore/input';
import {
  getResultsPaginatedNonRealtime,
  handleResultsProcess,
  resultSnapshot,
  ResultFilter,
} from '../../api/firebase/firestore/result';
import { getUser } from '../../api/firebase/firestore/user';
import { getFileUrl } from '../../api/firebase/storage';
import {
  gray100,
  gray20,
  gray40,
  gray80,
  orange50,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  tertiaryButtonBackgroundColorWhiteOverlay80,
  white,
  white_overlay80,
} from '../../constants/colors';
import {
  boldMd,
  boldMdGray,
  boldMdWhite,
  regularLg,
  regularLgWhite,
  regularMd,
  regularMdWhite,
  regularSm,
  regularXl,
} from '../../constants/location';
import { aiOcrResultMainInfoSchema } from '../../validation/aiocrResult';
import {
  AiOcrResultFiledType,
  GetResultDataType,
} from '../../validation/aiocrResult/aiOcrResultType';
import { InputFileType } from '../../validation/input/inputType';
import { AuthContext } from '../../contexts/auth';
import { GroupContext } from '../../contexts/group';
import CustomDatePicker from '../uiParts/CustomDatePicker';
import CustomLoading from '../uiParts/CustomLoading';
import CustomNumberField from '../uiParts/CustomNumberField';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import CustomTextfield from '../uiParts/CustomTextfield';
import DeleteModal from '../uiParts/DeleteModal';
import ImageOverlay from '../uiParts/ImageOverlay';
import SideBar from '../uiParts/SideBar';
import { FirebaseError } from 'firebase/app';
import { processingStatus, resultStatus } from '../../constants/enum';
import NoAccountIcon from '../assets/Account circle.svg';
import MetaTitle from '../utils/MetaTitle';
import { buildMinusSearchQuery } from '../../modules/minusSearch';
import { WatchSearchItemsType } from '../uiParts/AiOcrSearchForm';
import dayjs from 'dayjs';

const styles = {
  backGround: {
    height: '100vh',
    width: '100%',
    display: 'flex',
  },

  leftContainer: {
    backgroundColor: gray20,
    '@media (min-width: 960px)': {
      overflowY: 'auto',
      height: '100vh',
    },
    '@media (max-width: 960px)': {
      heigh: 'auto',
    },
  },

  rightContainer: {
    backgroundColor: gray100,
    height: '100vh',
    padding: '24px 16px',
    '@media (min-width: 960px)': {
      overflowY: 'auto',
    },
    '@media (max-width: 960px)': {
      height: 'auto',
      position: 'relative',
      paddingBottom: '72px',
      '&::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 0,
        left: 0,
        height: 'inherit',
        zIndex: -1,
      },
    },
  },

  leftHeader: {
    backgroundColor: white,
    padding: '18px 24px',
    height: 'auto',
    margin: '0px 2px 16px 0px',
    '@media (max-width: 960px)': {
      padding: '10px 12px',
      margin: '0px 0px 8px 0px',
    },
  },

  KeyboardArrowLeftButton: {
    color: gray80,
    padding: '10px 12px 10px 4px',
    height: '28px',
    '@media (max-width: 960px)': {
      height: '16px',
      width: '16px',
    },
  },

  KeyboardArrowRightButton: {
    color: gray80,
    padding: '10px 4px 10px 12px',
    height: '28px',
    '@media (max-width: 960px)': {
      height: '16px',
      width: '16px',
    },
  },

  closeButton: {
    color: gray80,
    padding: '10px 4px',
    height: '28px',
    minWidth: '32px',
  },

  staffName: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    margin: '0px 34px 16px 0px',
    gap: '4px',
    '@media (max-width: 960px)': {
      margin: '0px 16px 8px 0px',
    },
  },
  deliveryMainInfo: {
    backgroundColor: white,
    margin: '0px 16px 16px 16px',
    padding: '16px',
    '@media (max-width: 960px)': {
      margin: '0px 0px 8px 0px',
    },
  },
  deliveryProductInfo: {
    backgroundColor: white,
    margin: '0px 16px 40px 16px',
    padding: '16px',
    width: 'unset',
    boxShadow: 0,
    '@media (max-width: 960px)': {
      margin: '0px 0px 40px 0px',
    },
  },
  addButtom: {
    float: 'left',
    color: orange50,
  },
  addButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },
  removeButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },

  tableCelldebtorAddress: {
    padding: '0px 4px',
    height: '58px',
  },
  tableCellProductId: {
    width: '128px',
    padding: '0px 4px',
  },
  tableCellRemark: {
    width: '112px',
    padding: '0px 4px',
  },
  tableCell: {
    padding: '0px 4px',
    width: '80px',
  },
  tableCellRemoveButton: {
    padding: '0px',
    width: '24px',
  },

  rightHeader: {
    backgroundColor: gray100,
    width: '100%',
  },

  cardMediaBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    '@media (max-width: 960px)': {
      margin: '16px 8px 32px',
    },
  },

  openInNewButton: {
    color: white,
    minWidth: '32px',
  },

  downloadButton: {
    color: white,
    minWidth: '32px',
  },

  buttonsDisplayNoneSp: {
    '@media (max-width: 960px)': {
      display: 'none',
    },
  },

  footerButtons: {
    backgroundColor: white_overlay80,
    padding: '16px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '@media (min-width: 960px)': {
      display: 'none',
    },
  },

  footerTwoButtons: {
    width: '100%',
    display: 'flex',
    gap: '12px',
  },

  leftHeaderIconSize: {
    fontSize: '24px',
    '@media (max-width: 960px)': {
      fontSize: '16px',
    },
  },
  textCss: {
    ...boldMdGray,
    '@media (max-width: 960px)': {
      ...regularSm,
    },
  },

  fileTitleTextCss: {
    ...regularLgWhite,
    '@media (max-width: 960px)': {
      ...regularMdWhite,
    },
  },

  rightHeaderIconSize: {
    fontSize: '32px',
    '@media (max-width: 960px)': {
      fontSize: '24px',
    },
  },
  draftButtonText: {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      padding: '32px',
      margin: '0',
      width: '713px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
        width: '100%',
      },
    },
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  tableContainer: {
    minWidth: '100%',
    overflowX: 'auto',
    margin: '0px',
    padding: '0px',
    border: '1px solid #E0E0E0',
  },
  tableHeader: {
    padding: '8px 10px 8px 0',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '35px',
  },
  modalTableCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px 8px 0',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    minWidth: '35px',
  },
  modalButtonCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px 8px 0',
    width: '44px',
  },
  buttonHeight: {
    display: 'flex',
    width: '44px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: '16px',
  },
  idRemoveButton: {
    display: 'flex',
    width: '94px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '12px',
  },
};

const theme = createTheme({
  components: {
    MuiTableRow: {
      styleOverrides: {
        root: {
          minHeight: '56px',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1536,
    },
  },
});

const AiOcrResult: React.FC = () => {
  const { resultId, groupId } = useParams();
  const { groups } = useContext(GroupContext);
  const { user, isLoading } = useContext(AuthContext);

  // クエリ用のフィルタを計算
  const resultFilter: ResultFilter = useMemo(() => {
    if (groupId !== undefined) return groupId;
    return {
      createdBy: user?.uid ?? '',
      groupIds: groups.map(g => g.groupId),
    };
  }, [groupId, groups, user?.uid]);
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const [createUser, setCreateUser] = useState<string>('');
  const [icon, setIcon] = useState<string>('');
  const [files, setFiles] = useState<InputFileType[]>([]);
  const [originalFileUrl, setOriginalFileUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [resultData, setResultData] = useState<GetResultDataType | null>(null);
  const [focusItem, setFocusItem] = useState<string>('');
  const [status, setStatus] = useState<number | null>(null);
  const [isProcessingStatus, setIsProcessingStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [nextResultId, setNextResultId] = useState<string | null>(null);
  const [prevResultId, setPrevResultId] = useState<string | null>(null);
  const candidateCompaniesRef = useRef<HTMLTextAreaElement>(null);
  const candidateCompaniesByGeminiRef = useRef<HTMLTextAreaElement>(null);
  const [copiedMinusSearch, setCopiedMinusSearch] = useState(false);

  const [searchData, setSearchData] = useState<WatchSearchItemsType | undefined>(undefined);

  // 文字読み取り一覧画面から検索条件を取得
  useEffect(() => {
    if (location.state) {
      const searchData = location.state.searchData;
      setSearchData({
        ...searchData,
        startDate: searchData.startDate ? dayjs(searchData.startDate) : null,
        endDate: searchData.endDate ? dayjs(searchData.endDate) : null,
      });
    } else {
      setSearchData({
        debtorName: '',
        mansionName: '',
        responsiblePerson: '',
        establishmentStartDate: null,
        establishmentEndDate: null,
        startDate: null,
        endDate: null,
      });
    }
  }, [location.state]);

  const fetchData = async (): Promise<DocumentSnapshot | void> => {
    if (!resultId) return;
    // resultレコード取得
    const currentDocSnap = await resultSnapshot(resultId);
    const result = currentDocSnap.data();

    // 有効なレコードかチェック
    if (result?.status === resultStatus.deleted)
      navigate(groupId ? `/groups/${groupId}/ai-ocr/` : '/ai-ocr/');

    // 処理ステータスを取得
    setIsProcessingStatus(result?.processing_status);
    setStatus(result?.status);

    // inputレコード取得
    const response = await getInput(result?.inputId);

    // 作成者の名前を取得
    const createdUserId = response?.created_by;
    const userData = await getUser(createdUserId);
    setCreateUser(userData?.name);
    setIcon(userData?.iconUrl);

    // オリジナルファイルのURLを取得
    const originalFile = response?.original_files[0];
    let originalFileUrl = originalFile.file_url;
    if (!originalFile.file_url) {
      originalFileUrl = await getFileUrl(originalFile);
    }
    setOriginalFileUrl(originalFileUrl);

    // ファイル名を取得
    const fileName = response?.original_files[0]?.file_name;
    setFileName(fileName);

    // resultレコードの取得
    const resultData = result?.result_data;
    setResultData(resultData);
    await updateFilesWithUrls(response?.files);

    const setEstablishmentDate =
      resultData?.establishmentDate.value &&
      !isNaN(new Date(resultData?.establishmentDate.value).getTime())
        ? new Date(resultData?.establishmentDate.value)
        : null;

    // 値のセッティング
    reset({
      mansionName: resultData?.mansionName.value,
      propertyAddress: resultData?.propertyAddress.value,
      roomNumber: resultData?.roomNumber.value,
      previousOwnerCorporateName: resultData?.previousOwnerCorporateName?.value,
      establishmentDate: setEstablishmentDate ?? null,
      debtorAddress: resultData?.debtorAddress.value,
      debtorName: resultData?.debtorName.value,
      mortgageeCompanyName: resultData?.mortgageeCompanyName.value,
      loanAmount: resultData?.loanAmount.value || null,
      interestRate: resultData?.interestRate.value || null,
      candidateCompaniesByGemini: resultData?.candidateCompaniesByGemini?.value,
      candidateCompanies: resultData?.candidateCompanies?.value,
    });
    trigger();

    return currentDocSnap;
  };

  const getPrevNextResultId = async (currentDocSnap: DocumentSnapshot): Promise<void> => {
    const result = currentDocSnap.data();
    const status = result?.processing_status;

    // 次のresultIdを取得
    try {
      const { nextResultId, prevResultId } = await getResultsPaginatedNonRealtime(
        status,
        currentDocSnap,
        searchData,
        resultFilter,
      );
      setNextResultId(nextResultId ?? null);
      setPrevResultId(prevResultId ?? null);
    } catch (error) {
      setSnackbarText('データの取得に失敗しました。');
    }
  };

  useEffect(() => {
    void (async (): Promise<void> => {
      setLoading(true);
      // resultIdが変更された時はnextResultIdとprevResultIdをリセット
      setNextResultId(null);
      setPrevResultId(null);
      try {
        const currentDocSnap = await fetchData();
        if (currentDocSnap) {
          await getPrevNextResultId(currentDocSnap);
        }
      } catch (error) {
        if (error instanceof Error) {
          setErrorText(error.message);
        } else {
          setErrorText('データの取得に失敗しました。');
        }
      } finally {
        setLoading(false);
      }
    })();
    if (candidateCompaniesRef.current) {
      candidateCompaniesRef.current.scrollTop = 0;
    }
    if (candidateCompaniesByGeminiRef.current) {
      candidateCompaniesByGeminiRef.current.scrollTop = 0;
    }
  }, [resultId]);

  const {
    control,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<AiOcrResultFiledType>({
    resolver: yupResolver<AiOcrResultFiledType>(aiOcrResultMainInfoSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const buttonDisabledFlag = (): boolean => {
    return (
      watch('mansionName') === undefined ||
      '' ||
      watch('propertyAddress') === undefined ||
      '' ||
      watch('roomNumber') === undefined ||
      watch('debtorAddress') === undefined ||
      '' ||
      watch('debtorName') === undefined ||
      '' ||
      watch('mortgageeCompanyName') === undefined ||
      '' ||
      watch('loanAmount') === undefined ||
      watch('interestRate') === undefined ||
      Boolean(errors.mansionName) ||
      Boolean(errors.propertyAddress) ||
      Boolean(errors.roomNumber) ||
      Boolean(errors.establishmentDate) ||
      Boolean(errors.debtorAddress) ||
      Boolean(errors.debtorName) ||
      Boolean(errors.mortgageeCompanyName) ||
      Boolean(errors.loanAmount) ||
      Boolean(errors.interestRate) ||
      Boolean(dateError)
    );
  };

  // 更新系処理
  const handleResults = async (nextStatus: number, deleteFlg?: boolean): Promise<void> => {
    try {
      setLoading(true);
      if (!resultId) return;
      if (deleteFlg) {
        // 削除の場合は、削除ステータスに変更して画面遷移
        handleResultsProcess(resultId, watch, user, nextStatus, resultStatus.deleted);
        setIsModalOpen(false);
        const serializedSearchData = {
          ...searchData,
          startDate: searchData?.startDate ? searchData?.startDate.toISOString() : null,
          endDate: searchData?.endDate ? searchData?.endDate.toISOString() : null,
        };
        navigate(
          groupId ? `/groups/${groupId}/ai-ocr/delete/complete` : '/ai-ocr/delete/complete',
          {
            state: {
              prevId: prevResultId,
              nextId: nextResultId,
              searchData: serializedSearchData,
            },
          },
        );
        return;
      } else {
        // 処理ステータスを変更
        await handleResultsProcess(resultId, watch, user, nextStatus, status);
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'permission-denied':
            setErrorText('権限がありません。');
            break;
          case 'not-found':
            setErrorText('ドキュメントが見つかりません。');
            break;
          default:
            setErrorText('更新ができませんでした。');
            break;
        }
      } else if (error instanceof Error) {
        setErrorText(error.message);
      } else {
        setErrorText('更新ができませんでした。');
      }
    } finally {
      await fetchData();
      setLoading(false);
      // スナックバーの表示
      if (nextStatus === processingStatus.draft) {
        setSnackbarText('下書き保存しました。');
      } else if (nextStatus === processingStatus.completed) {
        setSnackbarText('処理済にしました。');
      } else if (nextStatus === processingStatus.processing) {
        setSnackbarText('未処理にしました。');
      }
    }
  };

  const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = (): void => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = url;
    });
  };

  const updateFilesWithUrls = async (files: InputFileType[]): Promise<void> => {
    const updatedFiles = await Promise.all(
      files.map(async (file: InputFileType) => {
        let fileUrl = file.file_url;
        if (!fileUrl) {
          fileUrl = await getFileUrl(file);
        }
        try {
          const { width, height } = await getImageDimensions(fileUrl);
          return { ...file, file_url: fileUrl, file_width: width, file_height: height };
        } catch (error) {
          return { ...file, file_url: fileUrl };
        }
      }),
    );
    // Sort files by page number
    const sortedFiles = updatedFiles.sort((a, b) => (a.page ?? 0) - (b.page ?? 0));
    setFiles(sortedFiles);
  };

  const [isImageOverlayLoaded, setIsImageOverlayLoaded] = useState<boolean>(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const handleImageLoad = (index: number): void => {
    setLoadedImages(prev => new Set(prev).add(index));
  };

  useEffect(() => {
    if (loadedImages.size === files.length && files.length > 0) {
      setIsImageOverlayLoaded(true);
    }
  }, [loadedImages, files]);

  const saveButtonStyles = {
    buttonChange:
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
        ? { ...secondaryButtonBackgroundColor }
        : isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing
          ? { ...primaryButtonBackgroundColor }
          : {},
    textColorChange:
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
        ? { ...boldMd }
        : isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing
          ? { ...boldMdWhite }
          : {},
  };

  // ボタンのテキストをステータスで変更
  const buttonText = (): string => {
    if (
      isProcessingStatus === processingStatus.draft ||
      isProcessingStatus === processingStatus.processing
    ) {
      return '処理済みにする';
    } else if (
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
    ) {
      return '未処理にする';
    } else {
      return '処理済みにする';
    }
  };

  // ボタンの処理
  const handleProcessButton = (): void => {
    if (
      // 下書きと未処理の場合
      isProcessingStatus === processingStatus.draft ||
      isProcessingStatus === processingStatus.processing
    ) {
      // 処理済みにする
      handleResults(processingStatus.completed);
    } else if (
      // 処理済みと出力済みの場合
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
    ) {
      // 未処理にする
      handleResults(processingStatus.processing);
    }
  };

  // ファイルのダウンロード
  const handleDownload = async (): Promise<void> => {
    try {
      // ファイルをダウンロード
      const response = await fetch(originalFileUrl);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // ダウンロードリンクを作成してクリック
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // URLオブジェクトを解放
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setErrorText('ファイルのダウンロードに失敗しました。');
    }
  };

  //ファイルの表示
  const openFile = (): void => {
    const fileName = originalFileUrl.split('/').pop()?.split('?')[0];
    // ファイル名から拡張子を抽出
    const fileExtension = fileName?.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'pdf':
        // PDFの場合、Google Docs Viewerを使用
        window.open(
          `https://docs.google.com/viewer?url=${encodeURIComponent(originalFileUrl)}&embedded=true`,
          '_blank',
        );
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
        // 画像の場合、直接URLを開く
        window.open(originalFileUrl, '_blank');
        break;
      default:
        setErrorText('ファイルの表示に失敗しました。');
        break;
    }
  };

  // 日付の有効チェック
  const [dateError, setDateError] = useState<string | null>(null);
  useEffect(() => {
    if (String(watch('establishmentDate')) === 'Invalid Date') {
      setDateError('有効な日付を入力してください');
    } else {
      setDateError(null);
    }
  }, [watch('establishmentDate')]);

  useEffect(() => {
    if (isLoading) return;
    // 未ログイン状態の場合はログイン画面へ遷移
    if (user === null) navigate('/');
  }, [user, isLoading]);

  return (
    <ThemeProvider theme={theme}>
      <MetaTitle />
      <SideBar pcIsNone />
      <CustomLoading loading={loading || (!isImageOverlayLoaded && files.length > 0)} />
      <Grid2 container sx={styles.backGround}>
        <Grid2 size={{ xs: 12, md: 7 }} sx={styles.leftContainer}>
          <Grid2 container sx={styles.leftHeader}>
            <Grid2
              size={{ xs: 0, md: 8 }}
              display="flex"
              justifyContent="start"
              alignItems="center"
            >
              <Button
                sx={{
                  ...saveButtonStyles.buttonChange,
                  mr: 2,
                  padding: '10px 16px',
                  ...styles.buttonsDisplayNoneSp,
                }}
                disabled={buttonDisabledFlag()}
                onClick={(): void => handleProcessButton()}
              >
                <Typography sx={saveButtonStyles.textColorChange}>{buttonText()}</Typography>
              </Button>
              <Button
                sx={{
                  ...secondaryButtonBackgroundColor,
                  mr: 2,
                  padding: '10px 16px',
                  ...styles.buttonsDisplayNoneSp,
                }}
                disabled={buttonDisabledFlag()}
                onClick={(): void => {
                  if (!resultId) return;
                  handleResults(processingStatus.draft);
                }}
              >
                <Typography sx={styles.draftButtonText}>下書き保存</Typography>
              </Button>
              <Button
                sx={{
                  ...tertiaryButtonBackgroundColor,
                  mr: 2,
                  padding: '10px 12px',
                  ...styles.buttonsDisplayNoneSp,
                }}
                onClick={(): void => {
                  setIsModalOpen(true);
                }}
              >
                <DeleteOutlinedIcon />
                <Typography sx={boldMdGray}>削除</Typography>
              </Button>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }} display="flex" justifyContent="end" alignItems="center">
              {prevResultId && (
                <Button
                  sx={styles.KeyboardArrowLeftButton}
                  onClick={(): void => {
                    const serializedSearchData = {
                      ...searchData,
                      startDate: searchData?.startDate ? searchData?.startDate.toISOString() : null,
                      endDate: searchData?.endDate ? searchData?.endDate.toISOString() : null,
                    };

                    navigate(
                      groupId
                        ? `/groups/${groupId}/ai-ocr/result/${prevResultId}`
                        : `/ai-ocr/result/${prevResultId}`,
                      { state: { searchData: serializedSearchData } },
                    );
                  }}
                >
                  <KeyboardArrowLeftIcon sx={styles.leftHeaderIconSize} />
                  <Typography sx={styles.textCss}>前へ</Typography>
                </Button>
              )}
              {nextResultId && (
                <Button
                  sx={styles.KeyboardArrowRightButton}
                  onClick={(): void => {
                    const serializedSearchData = {
                      ...searchData,
                      startDate: searchData?.startDate ? searchData?.startDate.toISOString() : null,
                      endDate: searchData?.endDate ? searchData?.endDate.toISOString() : null,
                    };

                    navigate(
                      groupId
                        ? `/groups/${groupId}/ai-ocr/result/${nextResultId}`
                        : `/ai-ocr/result/${nextResultId}`,
                      { state: { searchData: serializedSearchData } },
                    );
                  }}
                >
                  <Typography sx={styles.textCss}>次へ</Typography>
                  <KeyboardArrowRightIcon sx={styles.leftHeaderIconSize} />
                </Button>
              )}
              <Button
                sx={styles.closeButton}
                onClick={(): void => {
                  navigate(groupId ? `/groups/${groupId}/ai-ocr` : '/ai-ocr');
                }}
              >
                <CloseIcon sx={styles.leftHeaderIconSize} />
              </Button>
            </Grid2>
          </Grid2>
          <Stack direction="row" sx={styles.staffName}>
            <Typography sx={regularSm} textAlign="center">
              担当者：
            </Typography>
            <Avatar alt={createUser} src={icon ?? NoAccountIcon} sx={{ width: 24, height: 24 }} />
            <Typography sx={regularMd} textAlign="center">
              {createUser}
            </Typography>
          </Stack>
          <Stack sx={styles.deliveryMainInfo} spacing={1}>
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  required
                  requiredText={'必須'}
                  error={Boolean(errors.mansionName)}
                  formMb="24px"
                  helperText={errors.mansionName?.message}
                  labelText="マンション名"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('mansionName')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="mansionName"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  required
                  requiredText={'必須'}
                  error={Boolean(errors.propertyAddress)}
                  formMb="24px"
                  helperText={errors.propertyAddress?.message}
                  labelText="物件住所"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('propertyAddress')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="propertyAddress"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  required
                  requiredText={'必須'}
                  error={Boolean(errors.roomNumber)}
                  formMb="24px"
                  helperText={errors.roomNumber?.message}
                  labelText="部屋番号"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('roomNumber')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="roomNumber"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomDatePicker
                  {...field}
                  error={Boolean(dateError) || Boolean(errors.establishmentDate)}
                  helperText={dateError || errors.establishmentDate?.message}
                  labelText="原因の設定年月日"
                  width="100%"
                  disabled={false}
                  isRequired={false}
                  onChange={(newValue): void => setValue('establishmentDate', newValue)}
                  onFocus={(): void => setFocusItem('establishmentDate')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="establishmentDate"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.debtorName)}
                  formMb="24px"
                  helperText={errors.debtorName?.message}
                  labelText="所有者(債務者)氏名"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('debtorName')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="debtorName"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.debtorAddress)}
                  formMb="24px"
                  helperText={errors.debtorAddress?.message}
                  labelText="所有者(債務者)住所"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('debtorAddress')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="debtorAddress"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.mortgageeCompanyName)}
                  formMb="24px"
                  helperText={errors.mortgageeCompanyName?.message}
                  labelText="抵当権者名"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('mortgageeCompanyName')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="mortgageeCompanyName"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomNumberField
                  {...field}
                  isRequired={false}
                  labelText="借入額"
                  error={Boolean(errors.loanAmount)}
                  helperText={errors.loanAmount?.message}
                  formMb="narrow"
                  value={watch('loanAmount')}
                  onFocus={(): void => setFocusItem('loanAmount')}
                />
              )}
              control={control}
              name="loanAmount"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomNumberField
                  {...field}
                  isRequired={false}
                  labelText="金利"
                  error={Boolean(errors.interestRate)}
                  helperText={errors.interestRate?.message}
                  formMb="narrow"
                  value={watch('interestRate')}
                  onFocus={(): void => setFocusItem('interestRate')}
                />
              )}
              control={control}
              name="interestRate"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.previousOwnerCorporateName)}
                  formMb="24px"
                  helperText={errors.previousOwnerCorporateName?.message}
                  labelText="前所有者法人名（分譲会社・販売会社）"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="51px"
                  onFocus={(): void => setFocusItem('previousOwnerCorporateName')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="previousOwnerCorporateName"
            />
            <Stack spacing={1} sx={{ marginTop: '16px', textAlign: 'left' }}>
              <Typography variant="body1" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                Facebookの氏名検索リンク
              </Typography>
              <Link
                href={
                  'https://www.facebook.com/search/top/?q=' +
                  resultData?.debtorName.value +
                  '&locale=ja_JP'
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-block',
                  fontSize: '12px',
                  color: 'text.primary',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'text.primary',
                  },
                  width: 'fit-content',
                }}
              >
                {'https://www.facebook.com/search/top/?q=' +
                  resultData?.debtorName.value +
                  '&locale=ja_JP'}
              </Link>
            </Stack>
            <Stack spacing={1} sx={{ marginTop: '16px', textAlign: 'left' }}>
              <Typography variant="body1" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                Eight検索画面のリンク
              </Typography>
              <Link
                href={'https://8card.net/myhome?page=1&sort=exchangeDate&tab=network'}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-block',
                  fontSize: '12px',
                  color: 'text.primary',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'text.primary',
                  },
                  width: 'fit-content',
                }}
              >
                {'https://8card.net/myhome?page=1&sort=exchangeDate&tab=network'}
              </Link>
            </Stack>
            {(resultData?.candidateCompaniesByGemini?.value ||
              resultData?.candidateCompanies?.value) &&
              ((): JSX.Element | null => {
                const minusSearchQuery = buildMinusSearchQuery({
                  candidateCompaniesByGemini: resultData?.candidateCompaniesByGemini?.value,
                  candidateCompanies: resultData?.candidateCompanies?.value,
                  debtorName: resultData?.debtorName?.value,
                  debtorAddress: resultData?.debtorAddress?.value,
                });
                if (!minusSearchQuery) return null;

                return (
                  <Stack spacing={1} sx={{ marginTop: '16px', textAlign: 'left' }}>
                    <Typography variant="body1" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      マイナス検索文字列
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          wordBreak: 'break-all',
                          flex: 1,
                          backgroundColor: '#f5f5f5',
                          padding: '8px',
                          borderRadius: '4px',
                        }}
                      >
                        {minusSearchQuery}
                      </Typography>
                      <Tooltip title={copiedMinusSearch ? 'コピーしました' : 'コピー'}>
                        <IconButton
                          size="small"
                          onClick={(): void => {
                            navigator.clipboard.writeText(minusSearchQuery);
                            setCopiedMinusSearch(true);
                            setTimeout(() => setCopiedMinusSearch(false), 2000);
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                );
              })()}
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  inputRef={candidateCompaniesByGeminiRef}
                  error={Boolean(errors.candidateCompaniesByGemini)}
                  multiline
                  rows={10}
                  height="280px"
                  formMb="24px"
                  helperText={errors.candidateCompaniesByGemini?.message}
                  labelText="勤務先候補①"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="280px"
                  onFocus={(): void => setFocusItem('candidateCompaniesByGemini')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="candidateCompaniesByGemini"
            />
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  inputRef={candidateCompaniesRef}
                  error={Boolean(errors.candidateCompanies)}
                  multiline
                  rows={10}
                  height="280px"
                  formMb="24px"
                  helperText={errors.candidateCompanies?.message}
                  labelText="勤務先候補②"
                  inputWidth="100%"
                  labelFontSize="12px"
                  spHeight="280px"
                  onFocus={(): void => setFocusItem('candidateCompanies')}
                  onBlur={(): void => setFocusItem('')}
                />
              )}
              control={control}
              name="candidateCompanies"
            />

            {resultData?.urlByGemini && resultData.urlByGemini.length > 0 && (
              <Stack spacing={1} sx={{ marginTop: '16px', textAlign: 'left' }}>
                <Typography variant="body1" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  勤務先候補①関連リンク
                </Typography>
                {resultData.urlByGemini.map((urlInfo, index) => (
                  <Link
                    key={index}
                    href={urlInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      width: 'fit-content',
                      maxWidth: '100%',
                      wordBreak: 'break-all',
                      fontSize: '12px',
                      color: 'text.primary',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'text.primary',
                      },
                      marginBottom: '4px',
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                      [{index + 1}] {urlInfo.title}
                    </Typography>
                    <br />
                    <Typography component="span" sx={{ fontSize: '10px', color: 'text.secondary' }}>
                      {urlInfo.domain}
                    </Typography>
                  </Link>
                ))}
              </Stack>
            )}

            {resultData?.urls && resultData.urls.length > 0 && (
              <Stack spacing={1} sx={{ marginTop: '16px', textAlign: 'left' }}>
                <Typography variant="body1" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  勤務先候補②関連リンク
                </Typography>
                {resultData.urls.map((url, index) => (
                  <Link
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      width: 'fit-content',
                      maxWidth: '100%',
                      wordBreak: 'break-all',
                      fontSize: '12px',
                      color: 'text.primary',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'text.primary',
                      },
                    }}
                  >
                    [{index + 1}] {url}
                  </Link>
                ))}
              </Stack>
            )}
          </Stack>
        </Grid2>
        <Grid2
          size={{ xs: 12, md: 5 }}
          sx={styles.rightContainer}
          container
          flexDirection={'row'}
          spacing={'18px'}
          alignContent={'flex-start'}
        >
          <Grid2 container sx={styles.rightHeader} spacing={1}>
            <Grid2 size={10} display="flex" justifyContent="start" alignItems="center">
              <Typography sx={styles.fileTitleTextCss} textAlign="left">
                {fileName}
              </Typography>
            </Grid2>
            <Grid2 size={2} display="flex" justifyContent="end" alignItems="center">
              <Button sx={styles.openInNewButton} onClick={openFile}>
                <OpenInNewIcon sx={styles.rightHeaderIconSize} />
              </Button>
              <Button sx={styles.downloadButton} onClick={handleDownload}>
                <DownloadIcon sx={styles.rightHeaderIconSize} />
              </Button>
            </Grid2>
          </Grid2>
          {resultData &&
            files.length > 0 &&
            files.map((file: InputFileType, index: number) => {
              if (!file.file_url || !file.file_width || !file.file_height) return null;
              return (
                <ImageOverlay
                  key={index}
                  imageUrl={file.file_url}
                  result={resultData}
                  imageWidth={file.file_width}
                  imageHeight={file.file_height}
                  focusedField={focusItem}
                  page={index + 1}
                  onLoad={(): void => handleImageLoad(index)}
                />
              );
            })}
        </Grid2>
      </Grid2>
      <Box sx={styles.footerButtons}>
        <Button
          sx={saveButtonStyles.buttonChange}
          disabled={buttonDisabledFlag()}
          onClick={(): void => handleProcessButton()}
        >
          <Typography sx={saveButtonStyles.textColorChange}>{buttonText()}</Typography>
        </Button>
        <Box sx={styles.footerTwoButtons}>
          <Button
            sx={{ ...secondaryButtonBackgroundColor, flexGrow: '4' }}
            onClick={(): void => {
              if (!resultId) return;
              handleResults(processingStatus.draft);
            }}
            disabled={buttonDisabledFlag()}
          >
            <Typography sx={styles.draftButtonText}>下書き保存</Typography>
          </Button>
          <Button
            sx={{
              ...tertiaryButtonBackgroundColorWhiteOverlay80,
              minWidth: '32px',
              padding: '0',
            }}
            onClick={(): void => {
              setIsModalOpen(true);
            }}
          >
            <DeleteOutlinedIcon />
          </Button>
        </Box>
      </Box>
      <DeleteModal
        open={isModalOpen}
        onClose={(): void => {
          setIsModalOpen(false);
        }}
        onDelete={async (): Promise<void> => {
          if (isProcessingStatus === null) return;
          const deleteFlg = true;
          handleResults(isProcessingStatus, deleteFlg);
        }}
        title="ファイル"
      />

      <CustomSnackbar
        autoHideDuration={6000}
        open={errorText !== '' || snackbarText !== ''}
        text={errorText}
        infoText={snackbarText}
        closeButton={(): void => {
          setSnackbarText('');
          setErrorText('');
        }}
        onClose={(): void => {
          setSnackbarText('');
          setErrorText('');
        }}
      />
    </ThemeProvider>
  );
};
export default AiOcrResult;
